import {
  Cancel as CancelIcon,
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import type { ColumnDef } from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AIChat, { type AIChatHandle } from "~/components/aiChat";
import ChatHistoryPanel from "~/components/aiChat/components/ChatHistoryPanel";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import ExportModal, { type TableExportData } from "~/components/Document/exportModal";
import KnowledgeIndicator from "~/components/Project/knowledge/KnowledgeIndicator";
import KnowledgeSidebar from "~/components/Project/knowledge/KnowledgeSidebar";
import Button from "~/components/ui/button";
import { TOAST_MESSAGES } from "~/constants/messages";
import CustomizeSpecificationTable from "~/features/customizeSpecification/customizeSpecificationTable";
import { AIChatApi } from "~/functions/api/aiChat";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import { useKnowledgeCount } from "~/hooks/useKnowledgeCount";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import DeleteIcon from "~/images/delete_row.svg?react";
import {
  acquireLock,
  addRow,
  clearError,
  deleteRow,
  fetchCustomizeSpecificationList,
  releaseLock,
  restoreFromCopyData,
  updateCustomizeSpecificationList,
  updateField,
  updateModifiedAt,
} from "~/slices/customizeSpecificationListSlice";
import { initializeSessions } from "~/slices/projectSlice";
import type { AppDispatch, RootState } from "~/store";
import chatStyles from "../systemProposal/styles.module.scss";
import styles from "./index.module.scss";

const CustomizeSpecification = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { error: projectError } = useProjectMembers();
  const { data, isEditable, isLoading, error, documentInfo } = useSelector(
    (state: RootState) => state.customizeSpecificationList,
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const { selectedId } = useParams<{ selectedId: string }>();
  const { canAccessChat } = useProjectPermission();

  // チャットアイコンの表示可否を判定
  const shouldShowChatIcon = canAccessChat();

  // ナレッジインジケーター用のカウント取得
  const { count: knowledgeCount, totalCount: knowledgeTotalCount } =
    useKnowledgeCount("customize-specification");

  // ナレッジサイドバーの状態
  const [isKnowledgeSidebarOpen, setIsKnowledgeSidebarOpen] = useState(false);

  // AIChat関連の状態
  const { currentSession } = useSelector((state: RootState) => state.project);
  const [showAIChat, setShowAIChat] = React.useState(true);
  const [showChatHistory, setShowChatHistory] = React.useState(false);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  // biome-ignore lint/suspicious/noExplicitAny: thread型はChatHistoryPanelの内部型に依存
  const [threads, setThreads] = React.useState<any[]>([]);
  const aiChatRef = React.useRef<AIChatHandle>(null);
  const [currentThreadId, setCurrentThreadId] = React.useState<string>("");

  const toggleEditMode = () => {
    if (isEditable) {
      dispatch(updateModifiedAt());
      dispatch(updateCustomizeSpecificationList());
    } else {
      dispatch(acquireLock());
    }
  };

  const handleCancel = async () => {
    await dispatch(releaseLock());
    dispatch(restoreFromCopyData()); // 編集前のデータに戻す
  };

  useNavigationPrompt(
    isEditable,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    async () => {
      await dispatch(updateCustomizeSpecificationList());
    },
    handleCancel,
  );

  const getExportData = (): TableExportData => {
    const name = "カスタマイズ仕様書";
    const headers = [
      "機能名",
      "適用アプリ名",
      "概要",
      "入力フィールド",
      "出力フィールド",
      "処理条件",
      "機能詳細",
      "設定情報",
      "イベント契機",
      "対応端末",
    ];
    const rows = data.map((row) =>
      headers.map((header) => row[header as keyof typeof row]?.toString() || ""),
    );
    return { headers, rows, name };
  };

  useEffect(() => {
    if (!currentProject) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    // URLパラメータから取得したIDを優先、なければReduxストアから取得
    const docId = selectedId || currentProject.outputs?.["customize-specification"]?.id;
    if (!docId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.CUSTOMIZE_SPEC_NOT_FOUND);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    dispatch(fetchCustomizeSpecificationList(docId));
    // fetchDocumentInfoは削除（fetchCustomizeSpecificationListで統合済み）
  }, [dispatch, currentProject, currentUser, navigate, selectedId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (projectError) {
      console.error("Project data loading error:", projectError);
      toast.error(TOAST_MESSAGES.DOCUMENT.PROJECT_LOAD_FAILED);
    }
  }, [projectError]);

  // Initialize session IDs (reinit on document change)
  const customizeSpecDocId = currentProject?.outputs?.["customize-specification"]?.id;
  useEffect(() => {
    if (currentProject?.id && currentUser?.id) {
      dispatch(initializeSessions(customizeSpecDocId || ""));
    }
  }, [currentProject?.id, currentUser?.id, customizeSpecDocId, dispatch]);

  // currentSession.threadId が変更されたら currentThreadId を更新
  useEffect(() => {
    if (currentSession?.threadId) {
      setCurrentThreadId(currentSession.threadId);
    }
  }, [currentSession?.threadId]);

  // 履歴を更新する共通関数
  const refreshThreadHistory = async () => {
    // スレッド作成時や手動リロード時に履歴を更新
    setHistoryLoading(true);
    try {
      const api = new AIChatApi();
      const agentId = "commonAgent";
      const rid = currentSession?.resourceId;
      if (!rid) return;
      const list = await api.getThreads(agentId, rid, 10);
      setThreads(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("[CustomizeSpecification] Failed to refresh thread history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditable) {
        if (!currentProject.outputs?.["customize-specification"]?.id) {
          toast.error(TOAST_MESSAGES.DOCUMENT.CUSTOMIZE_SPEC_NOT_FOUND);
          return;
        }

        event.preventDefault();
        event.returnValue = "";
      }
    };

    // beforeunload イベントの登録
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // クリーンアップ
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditable, currentProject]);

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const columns: ColumnDef<(typeof data)[0]>[] = [
    {
      accessorKey: "機能名",
      header: "機能名",
      meta: { width: "15%" },
      cell: ({ row, getValue }) => {
        const value = getValue() as string;
        return (
          // biome-ignore lint/a11y/noStaticElementInteractions: roleは動的に設定（編集モード時はstatic）
          <span
            className={isEditable ? "" : styles["functionNameLink"]}
            role={isEditable ? undefined : "button"}
            tabIndex={isEditable ? undefined : 0}
            onClick={() => {
              if (!isEditable) {
                if (
                  currentUser.organization_id &&
                  currentProject.id &&
                  currentProject.outputs?.["customize-specification"]?.id
                ) {
                  navigate(
                    `/${currentUser.organization_id}/project/${currentProject.id}/customize-specification/${currentProject.outputs?.["customize-specification"]?.id}/rowIndex=${row.index}`,
                  );
                } else {
                  console.error("Cannot navigate: missing orgId, projId, or specId", {
                    orgId: currentUser.organization_id,
                    projId: currentProject.id,
                    specId: currentProject.outputs?.["customize-specification"]?.id,
                  });
                  toast.error(TOAST_MESSAGES.DOCUMENT.NAVIGATION_ERROR_ID_NOT_FOUND);
                }
              }
            }}
            onKeyDown={(e) => {
              if (!isEditable && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                if (
                  currentUser.organization_id &&
                  currentProject.id &&
                  currentProject.outputs?.["customize-specification"]?.id
                ) {
                  navigate(
                    `/${currentUser.organization_id}/project/${currentProject.id}/customize-specification/${currentProject.outputs?.["customize-specification"]?.id}/rowIndex=${row.index}`,
                  );
                }
              }
            }}
            style={{ cursor: isEditable ? "default" : "pointer" }}
          >
            {value}
          </span>
        );
      },
    },
    { accessorKey: "概要", header: "概要", meta: { width: "30%" } },
    { accessorKey: "処理条件", header: "処理条件", meta: { width: "15%" } },
    { accessorKey: "対応端末", header: "対応端末", meta: { width: "10%" } },
    {
      accessorKey: "対応区分",
      header: "レクシン対応",
      meta: { width: "15%" },
      cell: ({ getValue }) => {
        if (isEditable) return null;
        const value = getValue() as string;
        const isSupported = value === "対応";
        return (
          <div className={styles["reqxignStatus"]}>
            {isSupported ? (
              <>
                <CheckCircleIcon sx={{ width: 18, height: 18, color: "#1976d2" }} />
                <span className={styles["reqxignSupported"]}>アプリ生成機能で生成できます</span>
              </>
            ) : (
              <>
                <CancelIcon sx={{ width: 18, height: 18, color: "#d32f2f" }} />
                <span className={styles["reqxignUnsupported"]}>アプリ生成機能では非対応です</span>
              </>
            )}
          </div>
        );
      },
    },
    /* Detail button column removed as per REQ-1378 design changes */
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) =>
        isEditable ? (
          <button
            type="button"
            onClick={() => dispatch(deleteRow(row.index))}
            style={{ cursor: "pointer" }}
          >
            <DeleteIcon width={16} height={16} />
          </button>
        ) : null,
      meta: { width: "50px" },
    },
  ];

  const handleHistoryClick = () => {
    const pathSegments = window.location.pathname.split("/");
    pathSegments.pop();
    navigate(`${pathSegments.join("/")}/history`);
  };

  return (
    <div className={styles["customizeSpecification"]}>
      {documentInfo && (
        <>
          <CommonDocumentHeader
            title="カスタマイズ仕様書"
            documentType="customize-specification"
            createdAt={documentInfo.createdAt}
            modifiedAt={documentInfo.modifiedAt}
            {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
            isEditable={isEditable}
            isLoading={isLoading}
            handleCancel={handleCancel}
            toggleEditMode={toggleEditMode}
            handleExportClick={handleExportClick}
            handleHistoryClick={handleHistoryClick}
            showEditButton={true}
            showExportButton={true}
          />
          {false && (
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
              <KnowledgeIndicator
                documentType="customize-specification"
                activeCount={knowledgeCount}
                totalCount={knowledgeTotalCount}
              />
            </div>
          )}
        </>
      )}
      {showExportModal && (
        <ExportModal
          documentType="customize-specification"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "table",
            exportData: getExportData(),
            name: "customize-specification",
          }}
        />
      )}

      <div
        style={{
          display: "flex",
          gap: "24px",
          alignItems: "stretch",
          position: "relative",
          minHeight: "calc(100vh - 196px)",
        }}
      >
        <div style={{ flex: "1 1 auto" }}>
          <CustomizeSpecificationTable
            data={data}
            columns={columns}
            updateData={(rowIndex, columnId, value) =>
              dispatch(updateField({ rowIndex, columnId, value }))
            }
            isEditable={isEditable}
          />
          {isEditable && (
            <Button
              label="追加"
              onClick={() => dispatch(addRow())}
              styleType="add-row"
              icon="addRowIcon"
            />
          )}
        </div>
        {!showAIChat && (
          <KnowledgeSidebar
            isOpen={isKnowledgeSidebarOpen}
            onClose={() => setIsKnowledgeSidebarOpen(false)}
            documentType="customize-specification"
          />
        )}
        {showAIChat && (
          <div style={{ display: "flex", alignItems: "stretch", flex: "0 0 auto" }}>
            <ChatHistoryPanel
              open={showChatHistory}
              placement="sidebar"
              title="履歴（最新10件）"
              loading={historyLoading}
              threads={threads}
              activeThreadId={currentThreadId}
              onClose={() => setShowChatHistory(false)}
              onRefresh={refreshThreadHistory}
              onLoadThread={async (t) => {
                const tid = t.id || t.threadId;
                if (tid) {
                  setCurrentThreadId(tid);
                }
                await aiChatRef.current?.loadThread(t, "agent");
              }}
            />
            <AIChat
              ref={aiChatRef}
              isOpen={showAIChat}
              onClose={() => setShowAIChat(false)}
              onPreviewDiff={() => {}}
              height="calc(100vh - 196px)"
              maxHeight="none"
              currentDocumentId={currentProject?.outputs?.["customize-specification"]?.id || ""}
              currentProjectId={currentProject?.id || ""}
              currentDocumentType="customize-specification"
              originalDocument=""
              threadId={currentSession?.threadId}
              resourceId={currentSession?.resourceId}
              onOpenHistory={async () => {
                setShowChatHistory(true);
                await refreshThreadHistory();
              }}
              onThreadUpdate={refreshThreadHistory}
              onThreadChange={(tid) => setCurrentThreadId(tid)}
            />
          </div>
        )}
      </div>

      {!showAIChat && !isEditable && !isKnowledgeSidebarOpen && shouldShowChatIcon && (
        <div className={chatStyles["floatingButtons"]}>
          <KnowledgeIndicator
            documentType="customize-specification"
            activeCount={knowledgeCount}
            totalCount={knowledgeTotalCount}
            compact
            onOpen={() => setIsKnowledgeSidebarOpen(true)}
          />
          <button
            type="button"
            className={chatStyles["chatButton"]}
            onClick={() => setShowAIChat(true)}
            aria-label="AIチャットを開く"
          >
            <ChatIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomizeSpecification;
